import React, { useContext, useState } from 'react';
import { NavLink } from 'react-router-dom';
import Modal from '../components/modal';
import Button from '../components/button';
import { Grid } from '@mui/material';
import Autocomplete from '../components/input/autocomplete';
import { PraksisstedApiContext } from '../api/PraksisstedApiContext';
import { useLoader } from '../api/useLoader';
import {
  NyRolleDto,
  PraksisstedbrukerDto,
  PraksisstedRollerDto,
  RolleDto,
  StudiestedRollerDto,
} from '../api/generated/praksissted-generated-api';
import { useIntl } from 'react-intl';
import translations from './rolleliste.i18n';
import { useFormatMessageFor } from '../i18n/translation';
import * as css from './rolleliste.module.scss';
import {
  BrukerrollerDto,
  HttpError,
} from '../api/generated/studiested-generated-api';
import Spinner from '../components/spinner/spinner';
import { PsaUserContext } from '../user/PsaUserStore';
import { FakeLockedInput } from '../components/input';
import {
  AlpNotificationType,
  NotificationFunctionContext,
} from '../components/notifications/notificationProvider';
import { uuidv4 } from '../util/uuidv4';
import {
  Table,
  TableCellTd,
  TableCellTh,
  TableHeader,
  TableMain,
  TableRow,
} from '../components/table/alpTable';
import { useActivityContextPSA } from './activitycontexts';

const RolleListe = () => {
  const { praksisaktivitet } = useActivityContextPSA();
  const {
    id: aktivitetsId,
    organisasjonsNavn: sted,
    klasse,
  } = praksisaktivitet;
  const { emne } = klasse;

  const { praksiskalenderApi, praksisstedApi } = useContext(
    PraksisstedApiContext,
  );

  const {
    data: rollerData,
    loading: rollerLoading,
    reload: rollerReload,
  } = useLoader(
    () =>
      praksiskalenderApi.praksisaktivitetIdRolleGet({
        pathParams: { id: aktivitetsId },
      }),
    [aktivitetsId],
  );

  const { data: brukereData, loading: brukereLoading } = useLoader(
    () => praksisstedApi.praksisstedbrukereGet(),
    [],
  );
  const { addNotification } = useContext(NotificationFunctionContext);

  const [modalVisible, setModalVisible] = useState(false);
  const [rolleType, setRolleType] = useState<PraksisstedRollerDto>();
  const [selectedUser, setSelectedUser] = useState<PraksisstedbrukerDto>();
  const [activeUhaCol, setActiveUhaCol] = useState<number>(-1);
  const [activePsaCol, setActivePsaCol] = useState<number>(-1);
  const [uhaTableRows, setUhaTableRows] = useState<JSX.Element[]>();
  const [psaTableRows, setPsaTableRows] = useState<JSX.Element[]>();

  const labels = useFormatMessageFor(translations);
  const { locale } = useIntl();
  const headersUha = [
    { title: labels.uhaHeader1 },
    { title: labels.header2 },
    { title: labels.header3 },
    { title: labels.header4 },
  ];

  const headersPsa = [
    { title: labels.psaHeader1 },
    { title: labels.header2 },
    { title: labels.header3 },
    { title: labels.header4 },
    { title: labels.header5 },
  ];

  const handleAddUserLabel = () => {
    switch (rolleType) {
      case PraksisstedRollerDto.Koordinator:
        return labels.modalHeaderKoordinator;
      case PraksisstedRollerDto.Veileder:
        return labels.modalHeaderVeileder;
      default:
        return '';
    }
  };

  const handleAddSuccessLabel = () => {
    switch (rolleType) {
      case PraksisstedRollerDto.Koordinator:
        return labels.addSuccessKoordinator;
      case PraksisstedRollerDto.Veileder:
        return labels.addSuccessVeileder;
      default:
        return '';
    }
  };

  const handleRemoveSuccessLabel = (rolleType: PraksisstedRollerDto) => {
    switch (rolleType) {
      case PraksisstedRollerDto.Koordinator:
        return labels.removeSuccessKoordinator;
      case PraksisstedRollerDto.Veileder:
        return labels.removeSuccessVeileder;
      default:
        return '';
    }
  };

  const studiestedbruker = (rolle: RolleDto) => {
    let tabHeader = 0;

    return (
      <TableRow
        key={`${rolle.type.studiestedrolle}_${rolle.etternavn}_${rolle.fornavn}_${rolle.epost}`}
      >
        <TableCellTd header={headersUha[tabHeader++].title}>
          <NavLink to={`/studiestedbrukerprofil/${rolle.id}`}>
            {rolle.fornavn + ' ' + rolle.etternavn}
          </NavLink>
        </TableCellTd>
        <TableCellTd header={headersUha[tabHeader++].title}>
          {rolle.type.studiestedrolle}
        </TableCellTd>
        <TableCellTd header={headersUha[tabHeader++].title}>
          {rolle.epost}
        </TableCellTd>
        <TableCellTd header={headersUha[tabHeader].title}>
          {rolle.telefonnummer}
        </TableCellTd>
      </TableRow>
    );
  };

  const handleDelete = (
    brukerId: string,
    rolleType: PraksisstedRollerDto | undefined,
  ) => {
    if (rolleType) {
      praksiskalenderApi
        .praksisaktivitetIdRolleDelete({
          pathParams: { id: aktivitetsId },
          queryParams: { praksisstedbrukerId: brukerId, type: rolleType },
        })
        .then(() => {
          addNotification({
            id: uuidv4(),
            type: AlpNotificationType.SUCCESS,
            message: handleRemoveSuccessLabel(rolleType),
          });
          rollerReload();
        })
        .catch((err: HttpError) => {
          addNotification({
            id: uuidv4(),
            type: AlpNotificationType.ERROR,
            message: err.body,
          });
        });
    }
  };

  const { psaUser: bruker } = useContext(PsaUserContext);
  const canWrite =
    bruker && bruker.hoeyesteRettighet !== BrukerrollerDto.Leserettighet;

  const praksisstedbruker = (rolle: RolleDto) => {
    const navn = rolle.fornavn + ' ' + rolle.etternavn;
    let tabHeader = 0;

    return (
      <TableRow
        key={`${rolle.type.praksisstedrolle}_${rolle.etternavn}_${rolle.fornavn}_${rolle.epost}`}
      >
        <TableCellTd header={headersPsa[tabHeader++].title}>
          {rolle.slettet ? (
            <>{navn}</>
          ) : (
            <NavLink to={`/myorganization/praksisstedbrukerprofil/${rolle.id}`}>
              {navn}
            </NavLink>
          )}
        </TableCellTd>
        <TableCellTd header={headersPsa[tabHeader++].title}>
          {rolle.type.praksisstedrolle}
        </TableCellTd>
        <TableCellTd header={headersPsa[tabHeader++].title}>
          {rolle.epost}
        </TableCellTd>
        <TableCellTd header={headersPsa[tabHeader++].title}>
          {rolle.telefonnummer}
        </TableCellTd>
        <TableCellTd header={headersPsa[tabHeader].title}>
          {canWrite && (
            <Button.Tertiary
              onClick={() => {
                handleDelete(rolle.id, rolle.type.praksisstedrolle);
              }}
            >
              {labels.removeUser}
            </Button.Tertiary>
          )}
        </TableCellTd>
      </TableRow>
    );
  };

  const cancelModal = () => {
    setModalVisible(false);
  };

  const saveModal = async () => {
    const rolle: NyRolleDto = {
      praksisstedbrukerId: selectedUser && selectedUser.id,
      type: rolleType,
    };

    praksiskalenderApi
      .praksisaktivitetIdRollePost({
        pathParams: { id: aktivitetsId },
        nyRolleDto: rolle,
      })
      .then(() => {
        addNotification({
          id: uuidv4(),
          type: AlpNotificationType.SUCCESS,
          message: handleAddSuccessLabel(),
        });
        setModalVisible(false);
        rollerReload();
      })
      .catch((err: HttpError) => {
        addNotification({
          id: uuidv4(),
          type: AlpNotificationType.ERROR,
          message: err.body,
        });
      });
  };
  const addRolesGroupButtons = (
    <div className={css.roleGroupButtonContainer}>
      {canWrite && (
        <>
          <Button.Secondary
            onClick={() => {
              setRolleType(PraksisstedRollerDto.Koordinator);
              setModalVisible(true);
            }}
          >
            {labels.addUserKoordinator}
          </Button.Secondary>
          <Button.Secondary
            onClick={() => {
              setRolleType(PraksisstedRollerDto.Veileder);
              setModalVisible(true);
            }}
          >
            {labels.addUserVeileder}
          </Button.Secondary>
        </>
      )}
    </div>
  );

  const cancelButton = (
    <Button.Tertiary key={'cancel-modal'} onClick={cancelModal}>
      {labels.cancel}
    </Button.Tertiary>
  );
  const saveButton = (
    <Button.Secondary
      key={'save-modal'}
      disabled={!selectedUser}
      onClick={saveModal}
    >
      {labels.confirm}
    </Button.Secondary>
  );

  function roleAlreadyExist(id: string, roleType: PraksisstedRollerDto) {
    return (rollerData ?? [])
      .filter((v: RolleDto) => v.type.praksisstedrolle === roleType)
      .map((v) => v.id)
      .includes(id);
  }

  const handlePraksisstedbrukereFilter = () => {
    switch (rolleType) {
      case PraksisstedRollerDto.Koordinator:
        return brukereData?.filter(
          (val) =>
            (val.tilgangsrolle === BrukerrollerDto.Administrator ||
              val.tilgangsrolle === BrukerrollerDto.Skriverettighet) &&
            !roleAlreadyExist(val.id, rolleType),
        );
      case PraksisstedRollerDto.Veileder:
        return brukereData?.filter(
          (val) => !roleAlreadyExist(val.id, rolleType),
        );
      default:
        return [];
    }
  };

  const filtrerteBrukere = handlePraksisstedbrukereFilter();

  const compare = Intl.Collator(locale || 'nb').compare;
  const sortRowsByRolle = (
    col: number,
    a: RolleDto,
    b: RolleDto,
    studiested: boolean,
  ) => {
    switch (col) {
      case 0:
        return compare(a.etternavn, b.etternavn);
      case 1:
        return compare(
          studiested ? a.type.studiestedrolle! : a.type.praksisstedrolle!,
          studiested ? b.type.studiestedrolle! : b.type.praksisstedrolle!,
        );
      case 2:
        return compare(a.epost, b.epost);
    }
    return 0;
  };

  const sortUhaByActiveCol = (col: number, ascending: boolean) => {
    setUhaTableRows(
      rollerData
        ?.filter(
          (rolle) =>
            rolle.type.studiestedrolle === StudiestedRollerDto.Underviser ||
            rolle.type.studiestedrolle === StudiestedRollerDto.Koordinator,
        )
        .sort((a, b) => {
          return ascending
            ? sortRowsByRolle(col, b, a, true)
            : sortRowsByRolle(col, a, b, true);
        })
        .map(studiestedbruker),
    );
  };

  const sortPsaByActiveCol = (col: number, ascending: boolean) => {
    setPsaTableRows(
      rollerData
        ?.filter(
          (rolle) =>
            rolle.type.praksisstedrolle === PraksisstedRollerDto.Veileder ||
            rolle.type.praksisstedrolle === PraksisstedRollerDto.Koordinator,
        )
        .sort((a, b) => {
          return ascending
            ? sortRowsByRolle(col, b, a, false)
            : sortRowsByRolle(col, a, b, false);
        })
        .map(praksisstedbruker),
    );
  };

  const canSortIndices = [0, 1, 2];

  const getUhaHeaders = () =>
    headersUha.map(({ title }, index) => (
      <TableCellTh
        key={title}
        colIndex={index}
        activeCol={index === activeUhaCol}
        setActiveCol={setActiveUhaCol}
        sort={sortUhaByActiveCol}
        canSort={canSortIndices.includes(index)}
      >
        {title}
      </TableCellTh>
    ));

  const getPsaHeaders = () =>
    headersPsa.map(({ title }, index) => (
      <TableCellTh
        key={title}
        colIndex={index}
        activeCol={index === activePsaCol}
        setActiveCol={setActivePsaCol}
        sort={sortPsaByActiveCol}
        canSort={canSortIndices.includes(index)}
      >
        {title}
      </TableCellTh>
    ));

  return (
    <>
      <Modal
        title={handleAddUserLabel()}
        subtitle={emne.kode + ' - ' + emne.navn}
        isOpen={modalVisible}
        extraClasses={[css.addRoleModal]}
        actionButtons={[cancelButton, saveButton]}
      >
        <FakeLockedInput label={labels.access} value={sted} />
        <Autocomplete
          id={'rolle-autocomplete'}
          loading={brukereLoading}
          label={labels.name}
          options={
            brukereData &&
            filtrerteBrukere &&
            filtrerteBrukere.map((bruker) => ({
              ...bruker,
              navn: `${bruker.fornavn} ${bruker.etternavn}`,
            }))
          }
          onChange={(event: object, value: PraksisstedbrukerDto) => {
            setSelectedUser(value);
          }}
        />
      </Modal>
      {rollerLoading && <Spinner />}
      {rollerData && (
        <Grid container={true}>
          <Grid item={true} xs={12}>
            <Table>
              <TableHeader>
                <TableRow top>{getUhaHeaders()}</TableRow>
              </TableHeader>
              <TableMain>
                {uhaTableRows ||
                  rollerData
                    .filter(
                      (rolle) =>
                        rolle.type.studiestedrolle ===
                          StudiestedRollerDto.Underviser ||
                        rolle.type.studiestedrolle ===
                          StudiestedRollerDto.Koordinator,
                    )

                    .map(studiestedbruker)}
              </TableMain>
            </Table>
          </Grid>
          <Grid item={true} xs={12}>
            <br />
            <br />
          </Grid>
          <Grid item={true} xs={12}>
            <Table>
              <TableHeader>
                <TableRow top>{getPsaHeaders()}</TableRow>
              </TableHeader>
              <TableMain>
                {psaTableRows ||
                  rollerData
                    .filter(
                      (rolle) =>
                        rolle.type.praksisstedrolle ===
                          PraksisstedRollerDto.Veileder ||
                        rolle.type.praksisstedrolle ===
                          PraksisstedRollerDto.Koordinator,
                    )
                    .map(praksisstedbruker)}
              </TableMain>
            </Table>
          </Grid>
          <Grid item={true} xs={6} />
          <Grid item={true} xs={6}>
            <div style={{ paddingTop: '16px' }}>{addRolesGroupButtons}</div>
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default RolleListe;
